import React, { useState } from 'react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Textarea } from './ui/Textarea';
import { CheckCircle, AlertCircle } from 'lucide-react';
// ─── FORMSPREE CONFIGURATION ───
// 1. Go to https://formspree.io and create a free account
// 2. Create two forms: one for "Waitlist" and one for "Contact"
// 3. Replace the IDs below with your actual Formspree form IDs
const WAITLIST_FORM_ID = 'xkovnwlq';
const CONTACT_FORM_ID = 'xdalonrq';
type FormStatus = 'idle' | 'submitting' | 'success' | 'error';
export function EarlyAccessTab() {
  const [waitlistStatus, setWaitlistStatus] = useState<FormStatus>('idle');
  const [contactStatus, setContactStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const handleWaitlistSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setWaitlistStatus('submitting');
    setErrorMessage('');
    const form = e.currentTarget;
    const data = {
      name: (form.elements.namedItem('name') as HTMLInputElement)?.value,
      email: (form.elements.namedItem('email') as HTMLInputElement)?.value,
      company: (form.elements.namedItem('company') as HTMLInputElement)?.value,
      _subject: 'New MCPGuard Waitlist Signup'
    };
    try {
      const response = await fetch(`https://formspree.io/f/${WAITLIST_FORM_ID}`, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }
      });
      if (response.ok) {
        setWaitlistStatus('success');
      } else {
        const result = await response.json();
        setWaitlistStatus('error');
        setErrorMessage(result?.errors?.[0]?.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setWaitlistStatus('error');
      setErrorMessage('Network error. Please check your connection and try again.');
    }
  };
  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setContactStatus('submitting');
    setErrorMessage('');
    const form = e.currentTarget;
    const data = {
      name: (form.elements.namedItem('name') as HTMLInputElement)?.value,
      email: (form.elements.namedItem('email') as HTMLInputElement)?.value,
      message: (form.elements.namedItem('message') as HTMLTextAreaElement)?.value,
      _subject: 'New MCPGuard Contact Message'
    };
    try {
      const response = await fetch(`https://formspree.io/f/${CONTACT_FORM_ID}`, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }
      });
      if (response.ok) {
        setContactStatus('success');
      } else {
        const result = await response.json();
        setContactStatus('error');
        setErrorMessage(result?.errors?.[0]?.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setContactStatus('error');
      setErrorMessage('Network error. Please check your connection and try again.');
    }
  };
  return <div className="max-w-4xl mx-auto py-12 px-4 animate-in fade-in duration-500" data-id="element-47">
      <div className="grid md:grid-cols-2 gap-12 md:gap-24" data-id="element-48">
        {/* Section 1: Waiting List */}
        <div className="space-y-6" data-id="element-49">
          <div data-id="element-50">
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-51">
              Get Early Access to MCPGuard
            </h2>
            <p className="text-gray-600" data-id="element-52">
              MCPGuard is currently in private preview. Join the waiting list to
              receive early access and product updates.
            </p>
          </div>

          {waitlistStatus === 'success' ? <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center animate-in zoom-in-95 duration-300" data-id="element-53">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" data-id="element-54" />
              <h3 className="text-lg font-semibold text-green-900 mb-2" data-id="element-55">
                You're on the list!
              </h3>
              <p className="text-green-700" data-id="element-56">
                We'll be in touch shortly with your access details.
              </p>
            </div> : <form onSubmit={handleWaitlistSubmit} className="space-y-4" data-id="element-57">
              <Input label="Name" name="name" placeholder="Jane Doe" required data-id="element-58" />
              <Input label="Work Email" name="email" type="email" placeholder="jane@company.com" required data-id="element-59" />
              <Input label="Company (Optional)" name="company" placeholder="Acme Corp" data-id="element-60" />

              {waitlistStatus === 'error' && <div className="flex items-center space-x-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-id="element-61">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" data-id="element-62" />
                  <span data-id="element-63">{errorMessage}</span>
                </div>}

              <Button type="submit" className="w-full" isLoading={waitlistStatus === 'submitting'} data-id="element-64">
                Request Access
              </Button>
            </form>}
        </div>

        {/* Divider for mobile */}
        <div className="md:hidden border-t border-gray-200" data-id="element-65" />

        {/* Section 2: Contact */}
        <div className="space-y-6" data-id="element-66">
          <div data-id="element-67">
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-68">
              Talk to Us
            </h2>
            <p className="text-gray-600" data-id="element-69">
              Have questions about AI authorization, auditability, or enterprise
              deployment? We'd love to talk.
            </p>
          </div>

          {contactStatus === 'success' ? <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center animate-in zoom-in-95 duration-300" data-id="element-70">
              <CheckCircle className="h-12 w-12 text-blue-500 mx-auto mb-4" data-id="element-71" />
              <h3 className="text-lg font-semibold text-blue-900 mb-2" data-id="element-72">
                Message Sent
              </h3>
              <p className="text-blue-700" data-id="element-73">
                Thank you for reaching out. Our team will respond within 24
                hours.
              </p>
            </div> : <form onSubmit={handleContactSubmit} className="space-y-4" data-id="element-74">
              <Input label="Name" name="name" placeholder="John Smith" required data-id="element-75" />
              <Input label="Email" name="email" type="email" placeholder="john@example.com" required data-id="element-76" />
              <Textarea label="Message" name="message" placeholder="How does MCPGuard handle..." required rows={4} data-id="element-77" />

              {contactStatus === 'error' && <div className="flex items-center space-x-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-id="element-78">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" data-id="element-79" />
                  <span data-id="element-80">{errorMessage}</span>
                </div>}

              <Button type="submit" variant="outline" className="w-full" isLoading={contactStatus === 'submitting'} data-id="element-81">
                Send Message
              </Button>
            </form>}
        </div>
      </div>
    </div>;
}